/**
 * Лента карточки — общая для карточки сделки и карточки лида: событие со
 * звонком (запись, расшифровка), чатом открытых линий, мессенджером,
 * сердечками; фильтр по виду. Спеки `docs/specs/2026-09-25-deal-card-design.md`,
 * `docs/specs/2026-09-25-lead-card-design.md`; узкий вид с отбором по типам для панели
 * расширения — `docs/specs/2026-10-05-ext-timeline-design.md`.
 */
import {
  ArrowLeftRight, ArrowRight, CalendarClock, Check, ChevronDown, CircleCheck, CirclePlus, FileText, Heart, ListChecks,
  MessageSquareText, MessageCircle, MessagesSquare, Phone, Play, Store, TriangleAlert, Wallet,
} from "lucide-react";
import { type ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import type { CardEvent, EventKind, EventLink, EventSrc, HeartItem } from "@/lib/dealCard";
import { NavMenu } from "@/nav/NavMenu";

const ICON: Record<EventKind, ReactNode> = {
  created: <CirclePlus size={14} aria-hidden />,
  stage: <ArrowRight size={14} aria-hidden />,
  call: <Phone size={14} aria-hidden />,
  chat: <MessagesSquare size={14} aria-hidden />,
  messenger: <MessageCircle size={14} aria-hidden />,
  activity: <ListChecks size={14} aria-hidden />,
  comment: <MessageSquareText size={14} aria-hidden />,
  visit: <Store size={14} aria-hidden />,
  fitting: <CalendarClock size={14} aria-hidden />,
  contract: <FileText size={14} aria-hidden />,
  payment: <Wallet size={14} aria-hidden />,
  move: <ArrowLeftRight size={14} aria-hidden />,
  heart: <Heart size={14} aria-hidden />,
};
/** Тип события — бейдж у каждой строки ленты; в подсказке — откуда данные. */
const KIND: Record<EventKind, { label: string; bitrix?: string; crm?: string }> = {
  created: { label: "создание", bitrix: "crm.lead / crm.deal" },
  stage: { label: "стадия", bitrix: "crm.stagehistory", crm: "b24Deal_changes" },
  call: { label: "звонок", bitrix: "voximplant (своя копия bitrix_call)", crm: "b24call" },
  chat: { label: "чат", bitrix: "открытые линии (своя копия bitrix_chat_session)" },
  messenger: { label: "мессенджер", crm: "sMessenger_msg, sMessenger_media; чаты — по телефону: sMessenger_contact, sPhone, привязки" },
  activity: { label: "дело", bitrix: "crm.activity" },
  comment: { label: "комментарий", bitrix: "crm.timeline.comment", crm: "sSerial_comment" },
  visit: { label: "клиент в салоне", crm: "sCustomer" },
  fitting: { label: "повторная примерка", crm: "b24Deal_visit" },
  contract: { label: "договор", crm: "sCustomer_dogovor, _history, _product, _product_history, _changes_price" },
  payment: { label: "оплата", crm: "sPayment" },
  move: { label: "выдача / возврат", crm: "sMove_deal" },
  heart: { label: "сердечки", crm: "sCustomer_product, _changes" },
};
const SRC: Record<EventSrc, string> = { bitrix: "Bitrix", crm: "CRM" };
const LINK: Record<EventLink, string> = {
  lead: "лид",
  deal: "сделка",
  phone: "по номеру",
  chat: "чат",
  salon: "",
  crm: "",
};

/** Фильтр ленты по виду события: стадии и комментарии — в обоих. */
export type Scope = "all" | "kc" | "salon" | "alerts";
const SCOPE: { id: Scope; label: string }[] = [
  { id: "all", label: "Всё" },
  { id: "kc", label: "КЦ: звонки, чаты, мессенджеры, дела" },
  { id: "salon", label: "Салон: приходы, сердечки, примерки, договор, оплаты, выдача" },
  { id: "alerts", label: "Тревожные правки договора" },
];
const KC_KINDS: EventKind[] = ["call", "chat", "messenger", "activity"];
const SALON_KINDS: EventKind[] = ["visit", "heart", "fitting", "contract", "payment", "move"];
const inScope = (e: CardEvent, scope: Scope) =>
  scope === "all" ||
  (scope === "alerts" ? !!e.alerts?.length : !(scope === "kc" ? SALON_KINDS : KC_KINDS).includes(e.kind));

/** Отбор по типам события (`filter="kinds"`): порядок строк списка и их названия. */
const KIND_ORDER: EventKind[] = [
  "call", "comment", "messenger", "chat", "activity", "stage", "created", "visit", "fitting", "heart", "contract", "payment", "move",
];
const KIND_MANY: Record<EventKind, string> = {
  call: "Звонки",
  comment: "Комментарии",
  messenger: "Мессенджеры",
  chat: "Чаты Bitrix",
  activity: "Дела",
  stage: "Стадии",
  created: "Создание",
  visit: "Приходы в салон",
  fitting: "Повторные примерки",
  heart: "Сердечки",
  contract: "Договор",
  payment: "Оплаты",
  move: "Выдача и возврат",
};

/**
 * Где стоит лента. `compact` — узкая колонка (панель расширения, 240–320 px): дата — в строке
 * заголовка события, не отдельной колонкой. `base` — адрес кабинета, когда лента живёт вне его
 * (iframe расширения): ссылки на экраны кабинета — полным адресом в новой вкладке, запись звонка —
 * байтами через `fetch` (там его подменяет service worker с cookie кабинета; `<audio src>` cookie не несёт).
 */
const Place = createContext({ compact: false, base: "" });

export const dt = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "—");

/** То же с годом, когда он не текущий: в узкой ленте сделка за два года идёт одним списком. */
const dty = (iso: string) => (iso.slice(0, 4) === String(new Date().getFullYear()) ? dt(iso) : `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)} ${iso.slice(11, 16)}`);

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot={`итог «${label}»`}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[20px] font-semibold leading-none tabular-nums">{value}</div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

/** Запись звонка. Вне кабинета (`base`) — по кнопке: байты через `fetch`, плеер — из них. */
function CallRecord({ callId }: { callId: number }) {
  const { base } = useContext(Place);
  const [src, setSrc] = useState("");
  const [state, setState] = useState<"" | "load" | "error">("");
  useEffect(() => () => void (src && URL.revokeObjectURL(src)), [src]);
  if (!base) return <audio controls preload="none" src={`/api/calls/${callId}/record`} className="h-8 w-full max-w-md" />;
  if (src) return <audio controls autoPlay src={src} className="h-8 w-full" />;
  const load = async () => {
    setState("load");
    try {
      const r = await fetch(`/api/calls/${callId}/record`);
      if (!r.ok) throw new Error(String(r.status));
      setSrc(URL.createObjectURL(await r.blob()));
    } catch {
      setState("error");
    }
  };
  return (
    <button
      type="button"
      disabled={state === "load"}
      onClick={() => void load()}
      className={`inline-flex h-6 items-center gap-1 self-start text-[12px] hover:underline ${state === "error" ? "text-err" : "text-accent"}`}
    >
      <Play size={12} aria-hidden />
      {state === "load" ? "Загружаю запись…" : state === "error" ? "Запись не загрузилась — ещё раз" : "Прослушать"}
    </button>
  );
}

function CallBody({ e }: { e: CardEvent }) {
  const c = e.call!;
  const [open, setOpen] = useState(false);
  if (!c.transcript && !c.record) return null;
  return (
    <div className="mt-1 flex flex-col gap-1.5">
      {c.record && c.call_id && <CallRecord callId={c.call_id} />}
      {c.transcript && (
        <>
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="self-start text-[12px] text-accent hover:underline">
            {open ? "Скрыть расшифровку" : "Расшифровка"}
          </button>
          {open && <div className="whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg p-2 text-[12.5px] leading-relaxed">{c.transcript}</div>}
        </>
      )}
    </div>
  );
}

/** Строка переписки: время, кто пишет, текст — одной колонкой. Наш сотрудник —
 * акцентная полоса и имя цветом, клиент — нейтрально. Общая для чатов открытых
 * линий и мессенджеров. */
type Line = {
  key: string;
  at: string;
  side: "ours" | "client" | "other";
  who: string;
  text: string | null;
  note?: string | undefined;
  media?: { url: string | null; image: boolean } | undefined;
};

const SIDE = {
  ours: { bar: "border-accent", who: "text-accent" },
  client: { bar: "border-border-strong", who: "text-fg" },
  other: { bar: "border-border", who: "text-fg-muted" },
} as const;

/** Больше SHOW — показываем последние, остальное по кнопке; длинный текст
 * (шаблон с адресом салона) — 6 строк до «Развернуть». */
const SHOW = 20;
const LONG = 300;
function Transcript({ lines, day }: { lines: Line[]; day: string }) {
  const [all, setAll] = useState(false);
  const shown = all ? lines : lines.slice(-SHOW);
  const more = lines.length > SHOW || lines.some((l) => (l.text?.length ?? 0) > LONG);
  return (
    <div className="mt-1 flex flex-col gap-1">
      {more && (
        <button type="button" onClick={() => setAll(!all)} aria-expanded={all} className="self-start text-[12px] text-accent hover:underline">
          {all ? "Свернуть" : lines.length > SHOW ? `Показать все ${lines.length}` : "Развернуть"}
        </button>
      )}
      <ol className={`flex flex-col gap-1.5 rounded-[var(--radius-ui)] bg-bg p-2 text-[12.5px] ${all ? "" : "[&_.whitespace-pre-wrap]:line-clamp-6"}`}>
        {shown.map((l) => (
          <li key={l.key} className={`border-l-2 pl-2 ${SIDE[l.side].bar}`}>
            <div className="flex flex-wrap items-baseline gap-x-2 text-[11.5px]">
              <span className="tabular-nums text-fg-faint">{l.at.slice(0, 10) === day ? l.at.slice(11, 16) : dt(l.at)}</span>
              <span className={`font-medium ${SIDE[l.side].who}`}>{l.who}</span>
              {l.note && <span className="text-fg-faint">{l.note}</span>}
            </div>
            {l.media ? (
              l.media.url ? (
                l.media.image ? (
                  <a href={l.media.url} target="_blank" rel="noreferrer noopener">
                    <img src={l.media.url} alt="картинка из переписки" loading="lazy" className="mt-0.5 max-h-40 rounded" />
                  </a>
                ) : (
                  <a href={l.media.url} target="_blank" rel="noreferrer noopener" className="text-accent underline">
                    файл
                  </a>
                )
              ) : (
                <span className="text-fg-faint">файл без пути</span>
              )
            ) : (
              <div className="whitespace-pre-wrap break-words text-fg">{l.text || <span className="text-fg-faint">—</span>}</div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function ChatBody({ e }: { e: CardEvent }) {
  const msgs = e.chat!.messages.filter((m) => m.author !== "system");
  if (!msgs.length) return <div className="text-[12px] text-fg-faint">Переписка ещё не скачана.</div>;
  const lines: Line[] = msgs.map((m, i) => ({
    key: String(i),
    at: m.at,
    side: m.author === "operator" ? "ours" : "client",
    who: m.author === "operator" ? `Наш · ${m.name || "оператор"}` : `Клиент${m.name ? ` · ${m.name}` : ""}`,
    text: m.text,
  }));
  return <Transcript lines={lines} day={e.at.slice(0, 10)} />;
}

/** Переписка в мессенджере одним блоком (пауза > 3 ч — новый блок). */
function MessengerBody({ e }: { e: CardEvent }) {
  const { base } = useContext(Place);
  const m = e.messenger!;
  const end = m.end.slice(0, 10) === e.at.slice(0, 10) ? m.end.slice(11, 16) : dt(m.end);
  const lines: Line[] = m.items.map((i) => {
    const side = i.dir === "in" ? "client" : i.dir === "out" || i.dir === "sender" ? "ours" : "other";
    const role = side === "client" ? "Клиент" : i.dir === "sender" ? "Рассылка" : side === "ours" ? "Наш" : "Другое";
    return {
      key: `${i.kind}-${i.id}`,
      at: i.at,
      side,
      who: i.author ? `${role} · ${i.author}` : role,
      text: i.text,
      note: i.template ? `шаблон «${i.template}»` : undefined,
      media: i.kind === "media" ? { url: i.url, image: i.image } : undefined,
    };
  });
  return (
    <div className="mt-1 flex flex-col gap-1">
      <div className="flex flex-wrap gap-x-2 text-[11.5px] text-fg-faint">
        <span className="tabular-nums">до {end}</span>
        <span>входящих {m.in} · наших {m.out}</span>
        <span title={`чат найден: ${m.how.join("; ")}`}>как связан: {m.how.length}</span>
        {base ? (
          <a href={`${base}/messenger/chat/${m.chat_id}`} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            весь чат
          </a>
        ) : (
          <Link to={`/messenger/chat/${m.chat_id}`} className="text-accent hover:underline">
            весь чат
          </Link>
        )}
      </div>
      <Transcript lines={lines} day={e.at.slice(0, 10)} />
    </div>
  );
}

/** Сердечки: миниатюры с серией, статусом и ценой; клик — фото целиком. */
function Gallery({ items }: { items: HeartItem[] }) {
  return (
    <ul className="mt-1 flex flex-wrap gap-2" aria-label="товары в сердечках">
      {items.map((g, i) => {
        const tone = g.dislike ? "text-err" : g.like ? "text-ok" : "text-fg-muted";
        return (
          <li key={i} className={`flex w-[92px] flex-col gap-0.5 text-[11px] ${g.deleted ? "opacity-50" : ""}`} data-annot={`сердечко ${g.serial}`}>
            {g.image ? (
              <a href={g.image} target="_blank" rel="noreferrer noopener" title={g.name ?? String(g.serial)}>
                <img src={g.image} alt={`серия ${g.serial}`} loading="lazy" className="h-[120px] w-[92px] rounded-[var(--radius-ui)] border border-border bg-bg object-cover" />
              </a>
            ) : (
              <div className="flex h-[120px] w-[92px] items-center justify-center rounded-[var(--radius-ui)] border border-border text-fg-faint">нет фото</div>
            )}
            <span className="font-mono text-fg">{g.serial}</span>
            <span className={tone}>{g.status ?? "—"}{g.deleted ? " · удалён" : ""}</span>
            {g.price > 0 && <span className="tabular-nums text-fg-muted">{g.price.toLocaleString("ru-RU")} ₽</span>}
            <span className="tabular-nums text-fg-faint">{g.note}</span>
            {g.comment && <span className="text-fg-muted">{g.comment}</span>}
          </li>
        );
      })}
    </ul>
  );
}

export function Event({ e }: { e: CardEvent }) {
  const { compact } = useContext(Place);
  const talk = e.kind === "call" && e.call?.talk;
  const alert = !!e.alerts?.length;
  const tone = alert
    ? "text-err"
    : e.kind === "call" ? (talk ? "text-ok" : "text-fg-muted") : e.kind === "stage" ? "text-accent" : e.kind === "heart" ? "text-err" : "text-fg-muted";
  const k = KIND[e.kind];
  return (
    <li
      className={`grid border-t border-border first:border-t-0 ${compact ? "grid-cols-[0.875rem_1fr] gap-x-1.5 px-2 py-1.5" : "grid-cols-[5.5rem_1.25rem_1fr] gap-x-2 px-3 py-2"} ${alert ? "bg-err-bg" : ""}`}
      data-annot={`событие ${e.kind} ${dt(e.at)}`}
    >
      {!compact && <span className="whitespace-nowrap pt-px text-[12px] tabular-nums text-fg-faint">{dt(e.at)}</span>}
      <span className={`pt-0.5 ${tone}`}>{ICON[e.kind]}</span>
      <div className={`min-w-0 text-[12.5px] ${compact ? "[overflow-wrap:anywhere]" : ""}`}>
        <div className={`flex flex-wrap items-baseline ${compact ? "gap-x-1.5" : "gap-x-2"}`}>
          {compact && <span className="whitespace-nowrap text-[11.5px] tabular-nums text-fg-faint">{dty(e.at)}</span>}
          <span
            className="whitespace-nowrap rounded-[var(--radius-ui)] border border-border px-1.5 text-[11px] text-fg-muted"
            title={`${SRC[e.src]}: ${k[e.src] ?? "—"}`}
          >
            {compact ? k.label : `${SRC[e.src]} · ${k.label}`}
          </span>
          <span className={e.kind === "created" ? "font-medium" : ""}>{e.title}</span>
          {talk && (
            <span className="inline-flex items-center gap-0.5 text-ok">
              <CircleCheck size={11} aria-hidden /> <span className="sr-only">разговор</span>
            </span>
          )}
          {e.who && <span className="text-fg-muted">{e.who}</span>}
          {e.link && LINK[e.link] && (
            <span className={`text-[11.5px] ${e.link === "phone" ? "text-warn" : "text-fg-faint"}`} title={e.link === "phone" ? "звонок на номер клиента без привязки к этому лиду или сделке" : undefined}>
              {LINK[e.link]}
            </span>
          )}
          {e.refs?.map((r) => <NavMenu key={r.obj + r.id} r={r} />)}
          {e.kind === "activity" && e.done === false && <span className="text-[11.5px] text-warn">не завершено</span>}
        </div>
        {alert && (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[12px] font-medium text-err">
            <TriangleAlert size={12} aria-hidden />
            {e.alerts!.join(" · ")}
          </div>
        )}
        {e.text && <div className="whitespace-pre-wrap text-fg-muted">{e.text}</div>}
        {e.lines && e.lines.length > 0 && (
          <ul className="mt-0.5 text-[12px] text-fg-muted">
            {e.lines.map((l, i) => (
              <li key={i} className={`tabular-nums ${l.includes("⚠") ? "text-err" : ""}`}>
                · {l}
              </li>
            ))}
          </ul>
        )}
        {e.gallery && <Gallery items={e.gallery} />}
        {e.kind === "call" && <CallBody e={e} />}
        {e.kind === "chat" && <ChatBody e={e} />}
        {e.kind === "messenger" && <MessengerBody e={e} />}
      </div>
    </li>
  );
}

/**
 * Отбор ленты по типам события — выпадающий список с отметками: «Всё» или любой набор типов.
 * В списке только типы, которые в этой ленте есть, с числом событий; отметили все — это снова «Всё».
 */
function KindFilter({ events, picked, onChange }: { events: CardEvent[]; picked: EventKind[]; onChange: (kinds: EventKind[]) => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (ev: MouseEvent) => {
      if (!box.current?.contains(ev.target as Node)) setOpen(false);
    };
    const esc = (ev: KeyboardEvent) => ev.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const count = new Map<EventKind, number>();
  for (const e of events) count.set(e.kind, (count.get(e.kind) ?? 0) + 1);
  const kinds = KIND_ORDER.filter((k) => count.has(k));
  const on = kinds.filter((k) => picked.includes(k));
  const toggle = (k: EventKind) => {
    const next = on.includes(k) ? on.filter((x) => x !== k) : [...on, k];
    onChange(next.length === kinds.length ? [] : next);
  };
  const row = (key: string, label: string, n: number, checked: boolean, click: () => void) => (
    <button
      key={key}
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      onClick={click}
      className="flex h-7 w-full items-center gap-2 px-2.5 text-left text-[12.5px] text-fg hover:bg-bg-hover"
    >
      <span className={`grid size-3.5 flex-none place-items-center rounded-[3px] border ${checked ? "border-accent bg-accent text-white" : "border-border-strong"}`}>
        {checked && <Check size={11} strokeWidth={3} aria-hidden />}
      </span>
      <span className="truncate">{label}</span>
      <span className="ml-auto flex-none pl-2 text-[11.5px] tabular-nums text-fg-faint">{n}</span>
    </button>
  );
  return (
    <div ref={box} className="relative min-w-0" data-annot="отбор ленты по типам">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Что показать в ленте"
        className={`flex h-7 max-w-full items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[12.5px] ${open ? "border-border-strong" : "border-border hover:border-border-strong"}`}
      >
        <span className="flex h-full flex-none items-center border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">Показать</span>
        <span className={`truncate px-2 ${on.length ? "text-accent-text" : "text-fg"}`}>
          {on.length ? on.map((k) => KIND_MANY[k].toLowerCase()).join(", ") : "всё"}
        </span>
        <ChevronDown size={13} className="mr-1.5 flex-none text-fg-faint" aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute left-0 top-full z-30 mt-1 w-60 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated py-1 shadow-lg" data-popup="">
          {row("all", "Всё", events.length, on.length === 0, () => onChange([]))}
          <div className="my-1 border-t border-border" />
          {kinds.map((k) => row(k, KIND_MANY[k], count.get(k)!, on.includes(k), () => toggle(k)))}
        </div>
      )}
    </div>
  );
}

/**
 * Лента с фильтром: `scopes` — какие кнопки фильтра показать (у лида салона нет);
 * `newestFirst` — последнее событие сверху (панель «Чатов»), иначе по порядку.
 * `filter="kinds"` — вместо кнопок выпадающий отбор по типам события (`KindFilter`);
 * `compact` и `base` — узкая лента вне кабинета, см. `Place`.
 */
export function Timeline({
  events,
  scopes = SCOPE.map((s) => s.id),
  newestFirst = false,
  filter = "scope",
  compact = false,
  base = "",
}: {
  events: CardEvent[];
  scopes?: Scope[];
  newestFirst?: boolean;
  filter?: "scope" | "kinds";
  compact?: boolean;
  base?: string;
}) {
  const [scope, setScope] = useState<Scope>("all");
  const [kinds, setKinds] = useState<EventKind[]>([]);
  // Ключ строки — место события в полной ленте: смена отбора не переносит открытую расшифровку
  // и загруженную запись на чужой звонок.
  const shown = events
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => (filter === "kinds" ? !kinds.length || kinds.includes(e.kind) : inScope(e, scope)));
  if (newestFirst) shown.reverse();
  return (
    <Place.Provider value={{ compact, base }}>
      <div className="flex flex-wrap items-center gap-1.5 text-[12.5px]" role="group" aria-label="Что показать в ленте" data-annot="фильтр ленты">
        {filter === "kinds" ? (
          <KindFilter events={events} picked={kinds} onChange={setKinds} />
        ) : (
          scopes.length > 1 &&
          SCOPE.filter((o) => scopes.includes(o.id)).map((o) => (
            <button
              key={o.id}
              type="button"
              aria-pressed={scope === o.id}
              onClick={() => setScope(o.id)}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 ${scope === o.id ? "border-accent text-fg" : "border-border text-fg-muted hover:bg-bg-hover"}`}
            >
              {o.label}
            </button>
          ))
        )}
        <span className="self-center whitespace-nowrap text-fg-faint">{shown.length} событий</span>
      </div>
      <ol className="rounded-[var(--radius-card)] border border-border" data-annot="хронология">
        {shown.map(({ e, i }) => (
          <Event key={i} e={e} />
        ))}
      </ol>
    </Place.Provider>
  );
}
